import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './OrderDetail.css';

function OrderDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(true);
	const [expandedShipping, setExpandedShipping] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	const [showRefundModal, setShowRefundModal] = useState(false);  // ⭐ 추가

	useEffect(() => {
		fetchOrder();
	}, [id]);

	const fetchOrder = async () => {
		try {
			const token = localStorage.getItem('access_token');
			const response = await axios.get(
				`https://api.weartoday.kr/api/orders/${id}/`,
				{
					headers: { 'Authorization': `Bearer ${token}` }
				}
			);
			setOrder(response.data);
		} catch (error) {
			console.error('주문 조회 실패:', error);
			if (error.response?.status === 401) {
				navigate('/login');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleCancelOrder = async () => {
		if (!window.confirm('정말 주문을 취소하시겠습니까?\n취소 후에는 복구할 수 없습니다.')) {
			return;
		}

		setCancelling(true);
		try {
			const token = localStorage.getItem('access_token');
			await axios.patch(
				`https://api.weartoday.kr/api/orders/${id}/`,
				{ status: 'cancelled' },
				{ headers: { 'Authorization': `Bearer ${token}` } }
			);
			alert('주문이 취소되었습니다.');
			fetchOrder();
		} catch (error) {
			console.error('주문 취소 실패:', error);
			alert('주문 취소에 실패했습니다. 고객센터에 문의해주세요.');
		} finally {
			setCancelling(false);
		}
	};

	// ⭐ 환불 요청 - 페이지로 이동
	const handleRefundRequest = (item) => {
		navigate(`/refund/${order.id}/${item.id}`);
	};

	// ⭐ 상품 선택 모달 열기 (상품이 여러개일 때)
	const openRefundModal = () => {
		if (order.items.length === 1) {
			// 상품이 1개면 바로 이동
			handleRefundRequest(order.items[0]);
		} else {
			// 상품이 여러개면 선택 모달
			setShowRefundModal(true);
		}
	};

	const getImageUrl = (url) => {
		if (!url) return 'https://via.placeholder.com/100';
		if (url.startsWith('/media')) {
			return `https://api.weartoday.kr${url}`;
		}
		return url;
	};

	const getStatusInfo = (status) => {
		const statusMap = {
			'pending': { text: '결제대기', color: '#ffa500', icon: '⏳' },
			'paid': { text: '결제완료', color: '#4CAF50', icon: '✅' },
			'preparing': { text: '배송준비', color: '#2196F3', icon: '📦' },
			'shipping': { text: '배송중', color: '#9C27B0', icon: '🚚' },
			'delivered': { text: '배송완료', color: '#607D8B', icon: '✔️' },
			'confirmed': { text: '구매확정', color: '#00BCD4', icon: '🎉' },
			'cancelled': { text: '취소완료', color: '#f44336', icon: '❌' },
			'refund_requested': { text: '환불요청', color: '#ff9800', icon: '🔄' },
			'refunded': { text: '환불완료', color: '#795548', icon: '💰' }
		};
		return statusMap[status] || { text: status, color: '#999', icon: '📋' };
	};

	const getCourierName = (code) => {
		const couriers = {
			'cj': 'CJ대한통운',
			'hanjin': '한진택배',
			'lotte': '롯데택배',
			'logen': '로젠택배',
			'kdexp': '경동택배',
			'post': '우체국택배',
			'ems': 'EMS',
			'dhl': 'DHL',
			'fedex': 'FedEx',
			'ups': 'UPS',
			'sf': 'SF익스프레스',
			'cne': 'CNE',
			'yanwen': '연운익스프레스'
		};
		return couriers[code] || code;
	};

	const getTrackingUrl = (courier, trackingNumber) => {
		const urls = {
			'cj': `https://www.cjlogistics.com/ko/tool/parcel/tracking?gnbInvcNo=${trackingNumber}`,
			'hanjin': `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?wblnum=${trackingNumber}`,
			'lotte': `https://www.lotteglogis.com/home/reservation/tracking/invoice?InvNo=${trackingNumber}`,
			'logen': `https://www.ilogen.com/web/personal/trace/${trackingNumber}`,
			'post': `https://service.epost.go.kr/trace.RetrieveDomRi498.postal?sid1=${trackingNumber}`,
		};
		return urls[courier] || null;
	};

	// ⭐ 상품별 환불 상태 확인
	const getItemRefundStatus = (item) => {
		// item.refunds가 있으면 환불 신청된 상품
		if (item.refund_status) {
			return item.refund_status;
		}
		return null;
	};

	if (loading) {
		return (
			<div className="order-detail-page">
				<div className="loading">⏳ 주문 정보를 불러오는 중...</div>
			</div>
		);
	}

	if (!order) {
		return (
			<div className="order-detail-page">
				<div className="error">주문을 찾을 수 없습니다.</div>
				<button onClick={() => navigate('/orders')}>목록으로</button>
			</div>
		);
	}

	const statusInfo = getStatusInfo(order.status);
	const canRefund = ['paid', 'preparing', 'shipping', 'delivered'].includes(order.status);

	return (
		<div className="order-detail-page">
			<div className="order-detail-container">
				{/* 헤더 */}
				<div className="detail-header">
					<button className="btn-back" onClick={() => navigate('/orders')}>
						← 목록으로
					</button>
					<h1>주문 상세</h1>
				</div>

				{/* 주문 상태 */}
				<div className="status-section">
					<div className="status-icon">{statusInfo.icon}</div>
					<div className="status-info">
						<span className="status-text" style={{ color: statusInfo.color }}>
							{statusInfo.text}
						</span>
						<span className="order-number">주문번호: {order.order_number || order.id}</span>
						<span className="order-date">
							{new Date(order.created_at).toLocaleString('ko-KR')}
						</span>
					</div>
				</div>

				{/* 배송 정보 */}
				{order.shipping && (order.shipping.overseas_courier || order.shipping.courier) && (
					<div className="section shipping-section">
						<h2 onClick={() => setExpandedShipping(!expandedShipping)}>
							📦 배송정보 {expandedShipping ? '▲' : '▼'}
						</h2>
						{expandedShipping && (
							<div className="shipping-details">
								{order.shipping.overseas_courier && (
									<div className="shipping-row overseas">
										<span className="label">🌏 해외배송</span>
										<span className="courier">{getCourierName(order.shipping.overseas_courier)}</span>
										<span className="tracking">{order.shipping.overseas_tracking_number}</span>
										{order.shipping.overseas_shipped_at && (
											<span className="date">
												{new Date(order.shipping.overseas_shipped_at).toLocaleDateString('ko-KR')}
											</span>
										)}
									</div>
								)}
								{order.shipping.courier && (
									<div className="shipping-row domestic">
										<span className="label">🇰🇷 국내배송</span>
										<span className="courier">{getCourierName(order.shipping.courier)}</span>
										<span className="tracking">{order.shipping.tracking_number}</span>
										{getTrackingUrl(order.shipping.courier, order.shipping.tracking_number) && (
											<a 
												href={getTrackingUrl(order.shipping.courier, order.shipping.tracking_number)}
												target="_blank"
												rel="noopener noreferrer"
												className="tracking-link"
											>
												배송조회
											</a>
										)}
									</div>
								)}
								{order.shipping.delivered_at && (
									<div className="delivered-info">
										✅ 배송완료: {new Date(order.shipping.delivered_at).toLocaleString('ko-KR')}
									</div>
								)}
							</div>
						)}
					</div>
				)}

				{/* 주문 상품 - ⭐ 개별 환불 버튼 추가 */}
				<div className="section">
					<h2>🛍️ 주문 상품</h2>
					<div className="items-list">
						{order.items?.map((item, idx) => (
							<div key={idx} className="item-card">
								<img src={getImageUrl(item.product_image)} alt={item.product_name} />
								<div className="item-info">
									<h3>{item.product_name}</h3>
									{item.option_name && <p>옵션: {item.option_name}</p>}
									{item.size && <p>사이즈: {item.size}</p>}
									<p className="quantity">수량: {item.quantity}개</p>
									
									{/* ⭐ 환불 상태 표시 */}
									{item.refund_status && (
										<span className={`item-refund-badge ${item.refund_status}`}>
											{item.refund_status === 'requested' && '환불요청'}
											{item.refund_status === 'approved' && '환불승인'}
											{item.refund_status === 'completed' && '환불완료'}
											{item.refund_status === 'rejected' && '환불거절'}
										</span>
									)}
								</div>
								<div className="item-right">
									<div className="item-price">
										<span className="unit-price">{Number(item.price).toLocaleString()}원</span>
										<span className="total-price">
											{(Number(item.price) * item.quantity).toLocaleString()}원
										</span>
									</div>
									
									{/* ⭐ 개별 상품 환불 버튼 */}
									{canRefund && !item.refund_status && (
										<button 
											className="btn-item-refund"
											onClick={() => handleRefundRequest(item)}
										>
											환불신청
										</button>
									)}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* 배송지 정보 */}
				<div className="section">
					<h2>📍 배송지 정보</h2>
					<div className="address-info">
						<p><strong>수령인:</strong> {order.recipient_name}</p>
						<p><strong>연락처:</strong> {order.recipient_phone}</p>
						<p><strong>주소:</strong> [{order.postal_code}] {order.address} {order.address_detail}</p>
						{order.delivery_memo && <p><strong>배송메모:</strong> {order.delivery_memo}</p>}
					</div>
				</div>

				{/* 결제 정보 */}
				<div className="section">
					<h2>💳 결제 정보</h2>
					<div className="payment-info">
						<div className="payment-row">
							<span>상품금액</span>
							<span>{Number(order.total_amount).toLocaleString()}원</span>
						</div>
						{order.discount_amount > 0 && (
							<div className="payment-row discount">
								<span>할인금액</span>
								<span>-{Number(order.discount_amount).toLocaleString()}원</span>
							</div>
						)}
						<div className="payment-row">
							<span>배송비</span>
							<span>{Number(order.delivery_fee).toLocaleString()}원</span>
						</div>
						<div className="payment-row total">
							<span>총 결제금액</span>
							<span>{Number(order.final_amount).toLocaleString()}원</span>
						</div>
					</div>
				</div>

				{/* 버튼 영역 */}
				<div className="action-buttons">
					{/* 결제대기: 결제하기 + 주문취소 */}
					{order.status === 'pending' && (
						<>
							<button className="btn-pay">결제하기</button>
							<button 
								className="btn-cancel"
								onClick={handleCancelOrder}
								disabled={cancelling}
							>
								{cancelling ? '취소 중...' : '주문취소'}
							</button>
						</>
					)}

					{/* 결제완료: 주문취소 */}
					{order.status === 'paid' && (
						<button 
							className="btn-cancel"
							onClick={handleCancelOrder}
							disabled={cancelling}
						>
							{cancelling ? '취소 중...' : '주문취소'}
						</button>
					)}

					{/* 배송완료: 리뷰쓰기 */}
					{order.status === 'delivered' && (
						<button 
							className="btn-review"
							onClick={() => navigate(`/review/write?orderId=${order.id}`)}
						>
							리뷰쓰기
						</button>
					)}

					{/* 환불요청 상태 안내 */}
					{order.status === 'refund_requested' && (
						<div className="status-message">
							🔄 환불 요청이 접수되었습니다. 관리자 확인 후 처리됩니다.
						</div>
					)}

					{/* 취소/환불완료 안내 */}
					{['cancelled', 'refunded'].includes(order.status) && (
						<div className="status-message complete">
							✅ {order.status === 'cancelled' ? '주문이 취소되었습니다.' : '환불이 완료되었습니다.'}
						</div>
					)}
				</div>
			</div>

			{/* ⭐ 상품 선택 모달 (상품이 여러개일 때) */}
			{showRefundModal && (
				<div className="refund-modal-overlay" onClick={() => setShowRefundModal(false)}>
					<div className="refund-modal" onClick={e => e.stopPropagation()}>
						<div className="refund-modal-header">
							<h3>환불할 상품 선택</h3>
							<button className="modal-close" onClick={() => setShowRefundModal(false)}>×</button>
						</div>
						<div className="refund-modal-body">
							{order.items.map((item, idx) => (
								<div 
									key={idx} 
									className={`refund-item-select ${item.refund_status ? 'disabled' : ''}`}
									onClick={() => {
										if (!item.refund_status) {
											handleRefundRequest(item);
										}
									}}
								>
									<img src={getImageUrl(item.product_image)} alt={item.product_name} />
									<div className="refund-item-info">
										<p className="name">{item.product_name}</p>
										<p className="option">{item.option_name} / {item.size}</p>
										<p className="price">{Number(item.total_price).toLocaleString()}원</p>
									</div>
									{item.refund_status ? (
										<span className="already-refund">환불진행중</span>
									) : (
										<span className="select-arrow">→</span>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default OrderDetail;